import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  InputLabel, MenuItem, Select, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';

const FREQUENCIES = ['WEEKLY', 'MONTHLY', 'ONE_TIME', 'PER_EVENT'];
const label = (v: string) => v.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

interface Category {
  id: string;
  name: string;
  frequency: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  defaultAmount: number | null;
  currency: string;
  description: string | null;
  isSystem: number;
  active: number;
}

const empty = { name: '', frequency: 'MONTHLY', offeredBy: 'IN_HOUSE', defaultAmount: '', currency: 'USD', description: '' };

/** The catalog of bonus categories that the Bonus Tracker offers in its drop-down (managed by a super user). */
const BonusCatalogCard: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<{ id?: string; form: typeof empty } | null>(null);

  const load = useCallback(async () => {
    try {
      setCategories(await bonusTrackerAPI.catalog(true));
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load the bonus catalog.' });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    const { id, form } = editing;
    const input = { ...form, defaultAmount: form.defaultAmount === '' ? null : Number(form.defaultAmount) };
    try {
      if (id) await bonusTrackerAPI.updateCategory(id, input);
      else await bonusTrackerAPI.createCategory(input);
      setEditing(null);
      setMessage({ type: 'success', text: 'Bonus catalog saved.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not save the category.' });
    }
  };

  const toggle = async (category: Category) => {
    try {
      await bonusTrackerAPI.updateCategory(category.id, { active: !category.active });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not update the category.' });
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Categories</Typography>
          <Button variant="contained" size="small" onClick={() => setEditing({ form: { ...empty } })}>Add category</Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Offered by</TableCell>
              <TableCell align="right">Default amount</TableCell>
              <TableCell>Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  {c.name} {c.isSystem ? <Chip size="small" label="Read only" sx={{ ml: 1 }} /> : null}
                </TableCell>
                <TableCell>{label(c.frequency)}</TableCell>
                <TableCell>{c.offeredBy === 'CUSTOMER' ? 'Customer' : 'In house'}</TableCell>
                <TableCell align="right">{c.defaultAmount != null ? `${c.defaultAmount} ${c.currency}` : '-'}</TableCell>
                <TableCell>
                  <Switch size="small" checked={Boolean(c.active)} disabled={Boolean(c.isSystem)} onChange={() => toggle(c)} />
                </TableCell>
                <TableCell>
                  {!c.isSystem && (
                    <Button size="small" onClick={() => setEditing({
                      id: c.id,
                      form: { name: c.name, frequency: c.frequency, offeredBy: c.offeredBy, defaultAmount: c.defaultAmount != null ? String(c.defaultAmount) : '', currency: c.currency, description: c.description || '' },
                    })}>Edit</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          These categories fill the Category drop-down in the Bonus Tracker. Frequency and Offered by are only the defaults: each bonus can pick weekly or monthly and whether the customer pays it.
        </Typography>
      </CardContent>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Edit category' : 'Add category'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <TextField label="Name" value={editing?.form.name || ''} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, name: e.target.value } }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Frequency</InputLabel>
            <Select label="Frequency" value={editing?.form.frequency || 'MONTHLY'} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, frequency: String(e.target.value) } }))}>
              {FREQUENCIES.map((f) => <MenuItem key={f} value={f}>{label(f)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Offered by</InputLabel>
            <Select label="Offered by" value={editing?.form.offeredBy || 'IN_HOUSE'} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, offeredBy: String(e.target.value) } }))}>
              <MenuItem value="CUSTOMER">Customer offered (needs the customer answer and payment)</MenuItem>
              <MenuItem value="IN_HOUSE">In house</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Default amount" type="number" value={editing?.form.defaultAmount || ''} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, defaultAmount: e.target.value } }))} fullWidth />
            <TextField label="Currency" value={editing?.form.currency || 'USD'} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, currency: e.target.value.toUpperCase().slice(0, 3) } }))} sx={{ width: 140 }} />
          </Box>
          <TextField label="Description" value={editing?.form.description || ''} onChange={(e) => setEditing((p) => p && ({ ...p, form: { ...p.form, description: e.target.value } }))} fullWidth multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!editing?.form.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default BonusCatalogCard;
